import React, { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '@/src/lib/supabase';
import { Button } from '@/src/components/ui/button';
import { 
  X, 
  Printer, 
  RefreshCw, 
  FileText, 
  Check, 
  AlertCircle,
  Settings2,
  Share2,
  Send,
  Copy,
  ExternalLink,
  QrCode
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import BillPreview from '@/src/components/retail/BillPreview';
import { 
  BillData, 
  BillBlock, 
  renderBillHtml, 
  getBillPageCss 
} from '@/src/lib/billRenderer';

const DEFAULT_CLASSIC_BLOCKS: BillBlock[] = [
  { type: 'header', order: 1, visible: true, config: {} },
  { type: 'customer_info', order: 2, visible: true, config: {} },
  { type: 'invoice_meta', order: 3, visible: true, config: {} },
  { type: 'item_table', order: 4, visible: true, config: { show_rate: true, show_tax: true } },
  { type: 'totals', order: 5, visible: true, config: { show_tax_breakdown: true } },
  { type: 'footer', order: 6, visible: true, config: {} }
];

interface ServiceBillPrintModalProps {
  bookingId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ServiceBillPrintModal({
  bookingId,
  isOpen,
  onClose
}: ServiceBillPrintModalProps) {
  const supabase = getSupabaseClient();

  const [printerSize, setPrinterSize] = useState<string>('A4');
  const [styleName, setStyleName] = useState<string>('Classic');
  const [loading, setLoading] = useState<boolean>(true);
  const [printRawData, setPrintRawData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchPrintData = useCallback(async () => {
    if (!supabase || !bookingId) return;
    setLoading(true);
    setErrorMsg(null);

    try {
      const { data, error } = await supabase.rpc('get_service_invoice_print', {
        p_booking_id: bookingId,
        p_printer_size: printerSize,
        p_style: styleName
      });

      if (error) {
        console.error('get_service_invoice_print error:', error);
        setErrorMsg(error.message);
        toast.error('Bill print error: ' + error.message);
      } else if (data) {
        setPrintRawData(data);
      }
    } catch (err: any) {
      console.error('Failed to load bill print data:', err);
      setErrorMsg(err.message || 'Failed to load bill print data');
      toast.error(err.message || 'Failed to load bill print data');
    } finally {
      setLoading(false);
    }
  }, [supabase, bookingId, printerSize, styleName]);

  useEffect(() => {
    if (isOpen) {
      fetchPrintData();
    }
  }, [isOpen, fetchPrintData]);

  if (!isOpen) return null;

  // Map backend JSON to BillData expected by Retail renderBillHtml
  const buildBillData = (raw: any): BillData => {
    if (!raw) {
      return {
        business: { name: 'Business' },
        meta: { label: 'TAX INVOICE', number: 'INV-000', date: new Date().toLocaleDateString() },
        items: []
      };
    }

    const { header, invoice, booking, customer, lines } = raw;
    const branding = header?.branding || {};

    const formattedAddress = [
      header?.address,
      header?.city,
      header?.state,
      header?.pincode
    ].filter(Boolean).join(', ');

    // Filter service lines and material lines
    const serviceLines = (lines || []).filter((l: any) => l.kind === 'service');
    const materialLines = (lines || []).filter((l: any) => l.kind === 'material');

    const mappedItems = [
      ...serviceLines.map((l: any) => ({
        name: l.item_name,
        qty: l.quantity,
        unit: l.unit_name || 'Job',
        rate: Number(l.unit_price) || 0,
        tax_percent: Number(l.gst_rate) || 0,
        amount: Number(l.line_total) || 0
      })),
      ...materialLines.map((l: any) => ({
        name: `[Material] ${l.item_name}`,
        qty: l.quantity,
        unit: l.unit_name || 'pcs',
        rate: Number(l.unit_price) || 0,
        tax_percent: Number(l.gst_rate) || 0,
        amount: Number(l.line_total) || 0
      }))
    ];

    const taxBreakdown: any[] = [];
    if (invoice?.cgst && Number(invoice.cgst) > 0) {
      taxBreakdown.push({ label: 'CGST', amount: Number(invoice.cgst) });
    }
    if (invoice?.sgst && Number(invoice.sgst) > 0) {
      taxBreakdown.push({ label: 'SGST', amount: Number(invoice.sgst) });
    }
    if (invoice?.igst && Number(invoice.igst) > 0) {
      taxBreakdown.push({ label: 'IGST', amount: Number(invoice.igst) });
    }

    const extraMeta: any[] = [];
    if (booking?.booking_number) {
      extraMeta.push({ label: 'Booking Ref', value: booking.booking_number });
    }
    if (booking?.payment_status) {
      extraMeta.push({ label: 'Payment Status', value: String(booking.payment_status).toUpperCase() });
    }
    if (booking?.advance_amount && Number(booking.advance_amount) > 0) {
      extraMeta.push({ label: 'Advance Adjusted', value: `₹${Number(booking.advance_applied ?? booking.advance_amount).toFixed(2)}` });
    }
    if (invoice?.balance_due !== undefined && invoice?.balance_due !== null) {
      extraMeta.push({ label: 'Balance Due', value: `₹${Number(invoice.balance_due).toFixed(2)}` });
    }
    if (invoice?.amount_paid !== undefined && invoice?.amount_paid !== null) {
      extraMeta.push({ label: 'Amount Paid', value: `₹${Number(invoice.amount_paid).toFixed(2)}` });
    }

    return {
      business: {
        name: header?.business_name || 'Business Name',
        branch_name: header?.branch_name,
        address: formattedAddress,
        logo_url: header?.logo_url,
        stamp_url: branding?.stamp_url,
        show_branch_name: branding?.show_branch_name !== false,
        show_branch_address: branding?.show_branch_address !== false
      },
      customer: {
        name: customer?.name || 'Walk-in Customer',
        phone: customer?.phone || '',
        address: customer?.address || '',
        gstin: invoice?.customer_gstin || ''
      },
      customer_info_label: 'Bill To / Customer Details',
      meta: {
        label: 'SERVICE TAX INVOICE',
        number: invoice?.invoice_number || 'INV-PENDING',
        date: invoice?.invoice_date 
          ? new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
          : new Date().toLocaleDateString('en-IN'),
        extra: extraMeta
      },
      items: mappedItems,
      totals: {
        subtotal: Number(invoice?.taxable ?? invoice?.grand_total ?? 0),
        discount: Number(invoice?.discount ?? 0),
        tax_breakdown: taxBreakdown.length > 0 ? taxBreakdown : undefined,
        grand_total: Number(invoice?.grand_total ?? 0)
      },
      footer: {
        signature_label: 'Authorized Signatory',
        stamp_url: branding?.stamp_url
      },
      bill_number_code_type: branding?.bill_number_code_type || 'none',
      upi_payment: raw?.qr ? {
        payee_name: raw.qr.label || header?.business_name || 'Business',
        amount: Number(invoice?.balance_due && Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice?.grand_total || 0),
        qr_payload: raw.qr.payload,
        qr_label: raw.qr.label || (raw.qr.kind === 'pay_link' ? 'Scan to pay balance' : 'Scan to verify bill')
      } : header?.upi_id ? {
        upi_id: header.upi_id,
        payee_name: header.business_name,
        amount: Number(invoice?.balance_due && Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice?.grand_total || 0)
      } : undefined
    };
  };

  const billData = buildBillData(printRawData);
  const blocks: BillBlock[] = printRawData?.template?.blocks && Array.isArray(printRawData.template.blocks) && printRawData.template.blocks.length > 0
    ? printRawData.template.blocks
    : DEFAULT_CLASSIC_BLOCKS;

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied to clipboard!');
  };

  const handleShareWhatsApp = () => {
    if (!printRawData) return;
    const { header, invoice, customer, qr } = printRawData;
    const custPhone = customer?.phone ? customer.phone.replace(/\D/g, '') : '';
    const bizName = header?.business_name || 'Service Provider';
    const invNum = invoice?.invoice_number || 'INV';
    const total = invoice?.grand_total || 0;
    const balance = invoice?.balance_due ?? 0;

    let msg = `*${bizName} - Invoice ${invNum}*\n`;
    msg += `Customer: ${customer?.name || 'Valued Customer'}\n`;
    msg += `Total Amount: ₹${Number(total).toFixed(2)}\n`;
    if (Number(balance) > 0) {
      msg += `Balance Due: ₹${Number(balance).toFixed(2)}\n`;
    } else {
      msg += `Payment Status: Paid in full ✓\n`;
    }
    if (qr?.kind === 'pay_link' && qr?.payload) {
      msg += `\n*Pay Online via UPI / Card / QR:*\n${qr.payload}\n`;
    }
    msg += `\nThank you for choosing ${bizName}!`;

    const waUrl = custPhone 
      ? `https://wa.me/${custPhone}?text=${encodeURIComponent(msg)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');
    toast.success('Opening WhatsApp to share bill...');
  };

  const handlePrint = () => {
    if (!printRawData) return;
    const html = renderBillHtml(blocks, billData, printerSize);
    const pageCss = getBillPageCss(printerSize);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    if (win) {
      win.document.open();
      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${billData.meta.number}</title>
            <style>
              ${pageCss}
              body { margin: 0; padding: 0; }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            ${html}
          </body>
        </html>
      `);
      win.document.close();
      win.focus();

      setTimeout(() => {
        win.print();
        setTimeout(() => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 1000);
      }, 300);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 animate-in fade-in">
      <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <FileText size={18} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-base">
                Service Bill Print & Preview
              </h3>
              <p className="text-xs text-slate-500">
                Live Retail Blueprint renderer (A4 / Thermal / 80mm)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPrintData}
              disabled={loading}
              className="text-xs h-8 gap-1.5"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-900/60 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-600 dark:text-slate-300">Format / Size:</span>
              <select
                value={printerSize}
                onChange={e => setPrinterSize(e.target.value)}
                className="h-8 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 font-medium text-xs text-slate-900 dark:text-slate-100 outline-none"
              >
                <option value="A4">A4 (Standard Full Page)</option>
                <option value="A4-Half">A4-Half / A5 Document</option>
                <option value="112mm">4 Inch (112mm) Thermal</option>
                <option value="80mm">3 Inch (80mm) POS Thermal</option>
                <option value="58mm">2 Inch (58mm) Mini Thermal</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-600 dark:text-slate-300">Style:</span>
              <select
                value={styleName}
                onChange={e => setStyleName(e.target.value)}
                className="h-8 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 font-medium text-xs text-slate-900 dark:text-slate-100 outline-none"
              >
                <option value="Classic">Classic Template</option>
                <option value="Modern">Modern Template</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleShareWhatsApp}
              disabled={loading || !printRawData}
              className="text-xs h-8 px-3 font-semibold gap-1.5 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
            >
              <Share2 size={13} />
              <span>Share WhatsApp</span>
            </Button>
            <Button
              onClick={handlePrint}
              disabled={loading || !printRawData}
              className="bg-primary hover:bg-primary/90 text-white text-xs h-8 px-4 font-semibold gap-1.5 shadow-sm"
            >
              <Printer size={13} />
              <span>Print Bill</span>
            </Button>
          </div>
        </div>

        {/* Task 2: QR & Payment Share Banner */}
        {printRawData?.qr && (
          <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-indigo-950/40 border-b border-indigo-100 dark:border-indigo-900/40 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-3">
              <div className="bg-white p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 shadow-2xs shrink-0">
                <QRCodeSVG value={printRawData.qr.payload} size={48} level="M" />
              </div>
              <div>
                <div className="flex items-center gap-1.5 font-bold text-slate-900 dark:text-white">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    printRawData.qr.kind === 'pay_link'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                  }`}>
                    {printRawData.qr.kind === 'pay_link' ? '💳 Pay Link QR' : '🛡️ Verification QR'}
                  </span>
                  <span>{printRawData.qr.label}</span>
                </div>
                <div className="text-[11px] font-mono text-slate-500 dark:text-slate-400 truncate max-w-sm sm:max-w-md mt-0.5">
                  {printRawData.qr.payload}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {printRawData.qr.kind === 'pay_link' && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopyLink(printRawData.qr.payload)}
                  className="h-7 text-xs font-semibold gap-1 border-slate-300 dark:border-slate-700"
                >
                  <Copy size={11} />
                  <span>Copy Link</span>
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={handleShareWhatsApp}
                className="h-7 text-xs font-semibold gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Send size={11} />
                <span>Send to Customer</span>
              </Button>
            </div>
          </div>
        )}

        {/* Preview Scrollable Body */}
        <div className="flex-1 overflow-y-auto bg-slate-200/70 dark:bg-slate-950/80 p-4 sm:p-6 flex justify-center items-start">
          {loading ? (
            <div className="p-16 text-center text-slate-500 flex flex-col items-center justify-center gap-3">
              <RefreshCw size={26} className="animate-spin text-primary" />
              <span className="text-sm font-medium">Generating bill preview with blueprint...</span>
            </div>
          ) : errorMsg ? (
            <div className="p-8 max-w-md text-center bg-white dark:bg-slate-900 rounded-2xl border border-rose-200 dark:border-rose-900/60 shadow-lg space-y-3">
              <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/60 flex items-center justify-center mx-auto text-rose-600">
                <AlertCircle size={22} />
              </div>
              <h4 className="font-bold text-slate-900 dark:text-white text-base">
                Unable to load Invoice
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {errorMsg}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPrintData}
                className="text-xs mt-2"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="shadow-2xl rounded-sm overflow-hidden bg-white border border-slate-300 dark:border-slate-800">
              <BillPreview
                blocks={blocks}
                printerSize={printerSize}
                data={billData}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between text-xs text-slate-500">
          <span>
            Invoice: <strong className="font-mono text-slate-800 dark:text-slate-200">{printRawData?.invoice?.invoice_number || 'N/A'}</strong>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs h-8 px-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ServiceBillPrintModal;
